import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ROUTE_KEYS } from 'legacyfrontend/constants';
import { getCacheStatus } from 'legacyfrontend/helpers/getCacheStatus';
import { useGlobal } from 'legacyfrontend/contexts/GlobalContext';
import { clearFlightSelectionStateForNewSearch } from 'legacyfrontend/flights/search/flightSearchStorage';
import { updateFlightSearchState } from 'legacyfrontend/helpers/flightSearchStateStorage';
import { selectPaisId } from 'legacyfrontend/redux/selectors/application-selectors';
import { AutocompleteField } from '../shared/AutocompleteField';
import { CounterPopoverField } from '../shared/CounterPopoverField';
import { DateRangeField } from '../shared/DateRangeField';
import { FieldShell } from '../shared/FieldShell';
import { CurrencyToggle, SearchToggle, showsFlightCurrencyToggle } from '../shared/FlightToggles';
import { useAirportOptions } from './hooks/useAirportOptions';
import {
  EMPTY_FLIGHT_SEARCH,
  FlightSearchValues,
  buildFlightSearchQuery,
  parseFlightSearchValues,
  passengerAges,
  totalPassengers,
} from './flightSearchValues';
import { flightDraftKey, saveResolvedAirport, writeFlightSearchDraft } from './flightSearchDraft';
import {
  FlightSearchErrors,
  summarizeFlightErrors,
  validateFlightSearch,
} from './validateFlightSearch';
import {
  airportCountryLabel,
  airportKey,
  airportLabel,
  createCountryDisplayNames,
} from './airportOption';
import type { Airport } from './api/flights.types';
import type { SearcherFormProps } from '../types';
import '../shared/SearchFields.css';

const MAX_PASSENGERS = 9;

function passengersPreview(total: number): string {
  return total === 1 ? '1 pasajero' : `${total} pasajeros`;
}

/**
 * Real flights search, rendered inside the branding hero's search card.
 *
 * Layout matches prototipo_carga_header_v18: Solo ida replaces the "Fecha"
 * label (switch, not checkbox); USD/ARS sits above Buscar for Argentina.
 */
const FlightSearchForm: React.FC<SearcherFormProps> = ({
  isMobile = false,
  onSearchComplete,
  onSearchStart,
}) => {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { registerOnSearch } = useGlobal();
  const paisId = useSelector(selectPaisId);
  const showCurrency = showsFlightCurrencyToggle(paisId);

  const urlValues = useMemo(
    () =>
      pathname.replace(/\/$/, '') === ROUTE_KEYS.BUSCAR_X_VUELOS && search
        ? parseFlightSearchValues(search)
        : EMPTY_FLIGHT_SEARCH,
    [pathname, search],
  );

  const [values, setValues] = useState<FlightSearchValues>(urlValues);
  const [errors, setErrors] = useState<FlightSearchErrors>({});
  const [formMessage, setFormMessage] = useState('');
  const lastDraftKeyRef = useRef('');

  useEffect(() => {
    setValues(urlValues);
    setErrors({});
    setFormMessage('');
  }, [urlValues]);

  useEffect(() => {
    const key = flightDraftKey(values);
    if (key === lastDraftKeyRef.current) return;

    lastDraftKeyRef.current = key;
    writeFlightSearchDraft(values);
  }, [values]);

  const airports = useAirportOptions();
  const countryDisplayNames = useMemo(() => createCountryDisplayNames(), []);
  const getAirportGroup = useCallback(
    (airport: Airport) => airportCountryLabel(airport, countryDisplayNames),
    [countryDisplayNames],
  );

  const selectAirport = useCallback((field: 'origin' | 'destination', airport: Airport | null) => {
    setValues((prev) => ({ ...prev, [field]: airport }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));

    if (airport) saveResolvedAirport(field, airport);
  }, []);

  const handleOneWayChange = (isOnlyOneWay: boolean) => {
    setValues((prev) => ({
      ...prev,
      isOnlyOneWay,
      returnDate: isOnlyOneWay ? null : prev.returnDate,
    }));
    setErrors((prev) => ({ ...prev, dates: undefined }));

    try {
      updateFlightSearchState({ isOnlyOneWay });
    } catch (error) {
      console.warn('Error saving isOnlyOneWay to flightSearchState:', error);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validateFlightSearch(values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setFormMessage(summarizeFlightErrors(nextErrors));
      return;
    }

    setErrors({});
    setFormMessage('');
    onSearchStart?.();

    try {
      clearFlightSelectionStateForNewSearch({
        departureDate: values.departureDate,
        destination: values.destination,
        isOnlyOneWay: values.isOnlyOneWay,
        origin: values.origin,
        passengers: passengerAges(values.passengers),
        returnDate: values.returnDate,
      });
    } catch (error) {
      console.warn('Error clearing flight selection state for new search:', error);
    }

    const query = buildFlightSearchQuery(values, {
      cache: getCacheStatus(),
      includeRefresh: true,
    });

    history.push(`${ROUTE_KEYS.BUSCAR_X_VUELOS}${query}`);
    registerOnSearch?.();
    onSearchComplete?.();
  };

  const passengerTotal = totalPassengers(values.passengers);
  const remainingSeats = MAX_PASSENGERS - passengerTotal;

  return (
    <form
      aria-label='Buscador de vuelos'
      className={['searcher-form', isMobile && 'searcher-form--mobile'].filter(Boolean).join(' ')}
      noValidate
      onSubmit={handleSubmit}
    >
      <FieldShell
        error={errors.origin}
        errorId='flight-origin-error'
        glyph='⌖'
        htmlFor='flight-origin'
        label='Origen'
      >
        <AutocompleteField<Airport>
          emptyText='Escribí al menos 3 letras'
          errorId='flight-origin-error'
          filterMode='none'
          getOptionGroup={getAirportGroup}
          getOptionKey={airportKey}
          getOptionLabel={airportLabel}
          hasError={Boolean(errors.origin)}
          id='flight-origin'
          isLoading={airports.isLoadingOrigins}
          loadingText='Buscando aeropuertos…'
          onInputChange={(term) => airports.search(term, 'origin')}
          onSelect={(airport) => selectAirport('origin', airport)}
          options={airports.origins}
          placeholder='Ciudad o aeropuerto'
          value={values.origin}
        />
      </FieldShell>

      <FieldShell
        error={errors.destination}
        errorId='flight-destination-error'
        glyph='⌖'
        htmlFor='flight-destination'
        label='Destino'
      >
        <AutocompleteField<Airport>
          emptyText='Escribí al menos 3 letras'
          errorId='flight-destination-error'
          filterMode='none'
          getOptionGroup={getAirportGroup}
          getOptionKey={airportKey}
          getOptionLabel={airportLabel}
          hasError={Boolean(errors.destination)}
          id='flight-destination'
          isLoading={airports.isLoadingDestinations}
          loadingText='Buscando aeropuertos…'
          onInputChange={(term) => airports.search(term, 'destination')}
          onSelect={(airport) => selectAirport('destination', airport)}
          options={airports.destinations}
          placeholder='Ciudad o aeropuerto'
          value={values.destination}
        />
      </FieldShell>

      <FieldShell
        className='searcher-form__field-container-dates'
        error={errors.dates}
        errorId='flight-dates-error'
        glyph='▦'
        htmlFor='flight-dates'
        labelExtra={
          <SearchToggle
            checked={values.isOnlyOneWay}
            label='Solo ida'
            onChange={handleOneWayChange}
          />
        }
      >
        <DateRangeField
          errorId='flight-dates-error'
          hasError={Boolean(errors.dates)}
          id='flight-dates'
          isSingleDate={values.isOnlyOneWay}
          onChange={({ endDate, startDate }) => {
            setValues((prev) => ({
              ...prev,
              departureDate: startDate,
              returnDate: prev.isOnlyOneWay ? null : endDate,
            }));
            setErrors((prev) => ({ ...prev, dates: undefined }));
          }}
          triggerLabel='Fechas del viaje'
          value={{ endDate: values.returnDate, startDate: values.departureDate }}
        />
      </FieldShell>

      <FieldShell
        error={errors.passengers}
        errorId='flight-passengers-error'
        glyph='♙'
        htmlFor='flight-passengers'
        label='Pasajeros'
      >
        <CounterPopoverField
          counters={[
            {
              key: 'adults',
              label: 'Adultos',
              max: values.passengers.adults + remainingSeats,
              min: 1,
            },
            {
              key: 'children',
              label: 'Niños',
              max: values.passengers.children + remainingSeats,
              min: 0,
            },
            {
              key: 'infants',
              label: 'Bebés',
              max: values.passengers.infants + remainingSeats,
              min: 0,
            },
          ]}
          errorId='flight-passengers-error'
          hasError={Boolean(errors.passengers)}
          id='flight-passengers'
          onChange={(key, quantity) =>
            setValues((prev) => ({
              ...prev,
              passengers: { ...prev.passengers, [key]: quantity },
            }))
          }
          previewText={passengersPreview(passengerTotal)}
          triggerLabel='Cantidad de pasajeros'
          values={values.passengers as unknown as Record<string, number>}
        />
      </FieldShell>

      <div className='searcher-form__currency-actions'>
        {showCurrency ? (
          <CurrencyToggle
            currency={values.currency}
            onChange={(currency) => setValues((prev) => ({ ...prev, currency }))}
          />
        ) : null}
        <button className='searcher-form__submit' type='submit'>
          Buscar
        </button>
      </div>

      {formMessage && (
        <p aria-live='assertive' className='sr-only' role='alert'>
          {formMessage}
        </p>
      )}
    </form>
  );
};

export default FlightSearchForm;
